import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  TextField,
  IconButton,
  CircularProgress,
  Stack,
  Button,
  useTheme,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as CinchLogo } from '../cinch.svg';
import DeviceService from '../services/deviceService';
import CompanyLogo from '../components/CompanyLogo';
import { useSnackbar } from '../contexts/SnackbarContext';
import { useMember } from '../contexts/MemberContext';
import { useAuth } from '../contexts/AuthContext';
import CashierHeader from '../components/CashierHeader';
import KeypadContainer from '../components/KeypadContainer';

/**
 * Cashier login page for cashiers to input a 6-digit pin
 * Features a numeric keypad and pin display
 */
const CashierLoginPage = () => {
  const theme = useTheme();

  const [pin, setPIN] = useState('');
  const [loading, setLoading] = useState(false);
  const { showError } = useSnackbar();
  const navigate = useNavigate();

  const { orientation, setOrientation, selectedOffer } = useMember();

  useEffect(() => {
    if (orientation === 1) {
      // the device was flipped over so navigate to the cashier login page.
      navigate('/member-dashboard');
    }
  }, [orientation]);

  // Handle user icon click to switch to customer side
  const handlePersonClick = () => {
    setOrientation(1); // Flip orientation to show member dashboard
  };


  // Handle keypad button press
  const handleKeyPress = (value) => {
    // Only add digit if we have less than 6 digits
    if (pin.length < 6) {
      setPIN(prev => prev + value);
    }
  };

  // Handle pin submission
  const handleSubmit = async () => {
    // Validate pin
    if (pin.length !== 6) {
      showError('Please enter a valid 6-digit pin');
      return;
    }

    setLoading(true);

    try {
      await DeviceService.cashierLogin(pin);

      // Handle successful cashier login
      // Navigate to cashier coupon page if there's a selected offer, otherwise to transaction page
      if (selectedOffer) {
        navigate('/cashier-coupon', { state: { cashierPin: pin } });
      } else {
        navigate('/cashier-transaction', { state: { cashierPin: pin } });
      }

    } catch (err) {
      console.log(err);
      showError(err.message || 'Invalid cashier pin. Please try again.');
    }

    setLoading(false);
  };

  // Navigate back to member dashboard
  const handleHomeClick = () => {
    navigate('/landing');
  };

  // Format pin as ● characters for display
  const formatCodeForDisplay = (pin) => {
    return '●'.repeat(pin.length) + '○'.repeat(Math.max(0, 6 - pin.length));
  };

  return (
    <Container sx={{
      height: '100vh',
      width: '100vw',
      maxWidth: '100vw !important',
      display: 'flex',
      flexDirection: 'row',
      padding: '0 !important',
      margin: '0 !important',
    }}>
      <Grid container sx={{
        height: '100%',
        width: '100%'
      }}>
        <Grid item size={6} sx={{
          justifyContent: 'center',
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'row',
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '100%',
            width: '100%',
            px: 3
          }}>
            <CashierHeader />

            <Box>
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                align="center"
                fontWeight="bold"
                sx={{ mb: 6, mt: 6 }}
              >
                Cashier Login
              </Typography>

              <Typography
                variant="h6"
                component="h2"
                gutterBottom
                align="center"
                sx={{ mb: 4 }}
              >
                Please Enter 6-Digit Code
              </Typography>

              <TextField
                fullWidth
                variant="outlined"
                value={formatCodeForDisplay(pin)}
                InputProps={{
                  readOnly: true,
                  sx: {
                    fontSize: '2rem',
                    textAlign: 'center',
                    input: { textAlign: 'center', letterSpacing: '0.5em' }
                  },
                  endAdornment: loading && <CircularProgress size={24} />
                }}
              />

              {!!selectedOffer && (
                <Box mt={4} textAlign="center">
                  <Typography variant="body2" color={theme.palette.primary.main}>
                    Customer has selected a coupon for use.
                  </Typography>
                </Box>
              )}
            </Box>
            <Box pb={2} display="flex" justifyContent="center" alignItems="center">
              <Typography variant="body3" color="text.secondary">
                powered by
              </Typography>
              <Box ml={1} display="flex" alignItems="center">
                <CinchLogo
                  width={75}
                  style={{ marginRight: '4px' }}
                />
              </Box>
            </Box>
          </Box>
        </Grid>
        <Grid item size={6}>
          <KeypadContainer
            loading={loading}
            canSubmit={pin.length === 6}
            onKeyPress={handleKeyPress}
            onSubmit={handleSubmit}
            onBackspace={() => setPIN(prev => prev.slice(0, -1))}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default CashierLoginPage;